import React, { useState } from 'react'
import axios from 'axios'
import toast, { Toaster } from 'react-hot-toast'
import ReCAPTCHA from 'react-google-recaptcha'
import { Helmet } from 'react-helmet-async'
import { PhoneIcon, MailIcon, LocationMarkerIcon } from '@heroicons/react/outline'

import Container from '../ui/Container'
import SectionHeading from '../ui/SectionHeading'
import { businessInfo } from '../../data/site'

import './index.css'

function Contact({ page }) {
  const recaptchaRef = React.createRef()

  const [values, setValues] = useState({
    name: '',
    phone: '',
    subject: '',
    message: '',
    email: ''
  })

  const handleNameInputChange = (event) => {
    event.persist();
    setValues((values) => ({ ...values, name: event.target.value }));
  };
  const handleSubjectInputChange = (event) => {
    event.persist();
    setValues((values) => ({ ...values, subject: event.target.value }));
  };
  const handleEmailInputChange = (event) => {
    event.persist();
    setValues((values) => ({ ...values, email: event.target.value }));
  };
  const handlePhoneInputChange = (event) => {
    event.persist();
    setValues((values) => ({ ...values, phone: event.target.value }));
  };
  const handleMessageInputChange = (event) => {
    event.persist();
    setValues((values) => ({ ...values, message: event.target.value }));
  };

  const onChange = () => {
    handleSubmit()
  }

  const handleSubmit = async () => {
    const notify = (message) => toast.success(`${message}`);
    try {
      await axios.request({
        baseURL: 'https://meditex-email-service.herokuapp.com',
        method: 'post',
        data: {
          from: businessInfo.email,
          to: businessInfo.email,
          subject: values.subject,
          message: values.message,
          phone: values.phone,
          email: values.email
        }
      })
      setValues({ name: '', phone: '', subject: '', message: '', email: '' });
      notify("We would respond within 24 hours");
    } catch (error) {
      notify("Pls try again later");
      setValues({ name: '', phone: '', subject: '', message: '', email: '' });
    }
  };

  const inputClasses = "block appearance-none placeholder-gray-500 border border-gray-300 rounded-md w-full py-2.5 px-4 text-gray-700 leading-5 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-400"

  return (
    <>
      {page && (
        <Helmet>
          <title>Contact Us | Meditex Healthcare Hub Limited</title>
          <meta
            name="description"
            content="Get in touch with Meditex Healthcare Hub Limited — Ikorodu and Ogijo diagnostic centres, phone, email and online contact form."
          />
        </Helmet>
      )}

      {page && (
        <div className="bg-primary py-16">
          <Container>
            <SectionHeading eyebrow="Get in touch" title="Contact Us" light />
          </Container>
        </div>
      )}

      <Container className={page ? 'py-16' : 'py-16'} id={page ? undefined : 'contact'}>
        {!page && <SectionHeading eyebrow="Get in touch" title="Contact Us" />}
        <div className="flex flex-col lg:flex-row gap-10">
          <form
            className="w-full lg:w-3/5 bg-white p-6 sm:p-8 rounded-xl shadow-md border border-gray-100"
            onSubmit={(e) => { e.preventDefault(); recaptchaRef.current.execute(); }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className={inputClasses} type="text" placeholder="Name" required value={values.name} onChange={handleNameInputChange} />
              <input className={inputClasses} type="email" placeholder="Email" required value={values.email} onChange={handleEmailInputChange} />
              <input className={inputClasses} type="tel" placeholder="Mobile Number" required value={values.phone} onChange={handlePhoneInputChange} />
              <input className={inputClasses} type="text" placeholder="Subject" required value={values.subject} onChange={handleSubjectInputChange} />
            </div>
            <textarea
              className={`${inputClasses} resize-none h-32 mt-4`}
              placeholder="Message"
              required
              value={values.message}
              onChange={handleMessageInputChange}
            />
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey="6Ld92gEcAAAAAChdBIpw4Yycp0V11RPPD8WTmUk2"
              onChange={onChange}
            />
            <button className="w-full mt-4 text-white bg-accent hover:bg-accent-600 font-bold py-3 rounded-md text-base transition-colors">
              Send Message
            </button>
          </form>

          <div className="w-full lg:w-2/5 flex flex-col gap-6">
            <p className="text-gray-600">
              {businessInfo.name} is willing to partner with individuals or organizations to deliver quality
              diagnostic results. Feel free to reach us using any of these mediums:
            </p>
            <div className="flex items-start gap-3">
              <PhoneIcon className="h-5 w-5 text-accent shrink-0 mt-0.5" />
              <span className="text-gray-700">{businessInfo.phones.join(' | ')}</span>
            </div>
            <div className="flex items-start gap-3">
              <MailIcon className="h-5 w-5 text-accent shrink-0 mt-0.5" />
              <span className="text-gray-700">{businessInfo.email}</span>
            </div>
            {businessInfo.locations.map((loc) => (
              <div key={loc.name} className="flex items-start gap-3">
                <LocationMarkerIcon className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-primary">{loc.name}</p>
                  <p className="text-gray-600 text-sm">{loc.address}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {page && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
            {businessInfo.locations.map((loc) => (
              <div key={loc.name} className="rounded-xl overflow-hidden shadow-md border border-gray-100">
                <iframe
                  title={loc.name}
                  className="w-full h-56"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(loc.mapQuery)}&output=embed`}
                />
              </div>
            ))}
          </div>
        )}
      </Container>
      <Toaster />
    </>
  );
}

export default Contact
